import React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import request from '../../utils/request';

var currentNode = {
    parent: null,
    nodes: []
}

/**
 * Highlights all case-insensitive occurrences of each whitespace-separated
 * word in searchText within displayText. Returns an array of React elements,
 * or the original string if no match.
 */
function applyHighlightToDisplay(displayText, searchText) {
    if (!displayText || !searchText) return displayText;

    const tokens = searchText.toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return displayText;

    const lowerDisplay = displayText.toLowerCase();
    const ranges = [];

    for (const token of tokens) {
        let from = 0;
        let idx;
        while ((idx = lowerDisplay.indexOf(token, from)) !== -1) {
            ranges.push([idx, idx + token.length]);
            from = idx + token.length;
        }
    }

    if (ranges.length === 0) return displayText;

    ranges.sort((a, b) => a[0] - b[0]);
    const merged = [ranges[0]];
    for (let i = 1; i < ranges.length; i++) {
        const prev = merged[merged.length - 1];
        const curr = ranges[i];
        if (curr[0] <= prev[1]) {
            prev[1] = Math.max(prev[1], curr[1]);
        } else {
            merged.push(curr);
        }
    }

    const elements = [];
    let lastIndex = 0;
    for (const [start, end] of merged) {
        if (start > lastIndex) {
            elements.push(<span key={`t${lastIndex}`}>{displayText.substring(lastIndex, start)}</span>);
        }
        elements.push(<strong key={`h${start}`}>{displayText.substring(start, end)}</strong>);
        lastIndex = end;
    }
    if (lastIndex < displayText.length) {
        elements.push(<span key={`t${lastIndex}`}>{displayText.substring(lastIndex)}</span>);
    }

    return elements;
}

export default class AddressSearch extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            popoverOpen: false,
            dataSource: [],
            textValue: '',
            textNotFound: this.props.noResultsMessage || 'No results were found. Please change your search criteria.',
            popoverAnchorEl: null
        };

        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleOnFocus = this.handleOnFocus.bind(this);
        this.handleMenuItemSelected = this.handleMenuItemSelected.bind(this);
        this.handleRequestClosePopover = this.handleRequestClosePopover.bind(this);
        this.timeout = null;
    }

    handleBack() {
        if (currentNode.parent != null) {
            currentNode = currentNode.parent;
            this.setState({ dataSource: currentNode.nodes });
        }
    }

    doneTyping(text, containerId = null) {
        const self = this;
        const searchURL = this.props.endPointUrl;

        if (this.props.makeAuthRequest) {
            const completeURL = searchURL + '?keyword=' + encodeURIComponent(text) + (this.props.extraQueryString || '');
            request
                .getAuth(completeURL)
                .then((result) => {
                    const dataList = this.extractDataFromResult(result);
                    self.setState({ dataSource: dataList, popoverOpen: true, isloading: false });
                })
                .catch(e => {
                    self.setState({ dataSource: [this.createNoResultsItem()], popoverOpen: true, isloading: false });
                });
        }
        else if (containerId != null && this.props.supportsHierarchicalSearch) {
            // Hierarchical search (e.g., for address containers)
            const url = this.buildContainerSearchUrl(searchURL, containerId);
            request
                .get(url)
                .then((result) => {
                    const dataList = this.extractDataFromResult(result);
                    self.setState({ dataSource: dataList, popoverOpen: true, isloading: false });

                    if (this.props.supportsHierarchicalSearch) {
                        self.addNodeToSearchTree(dataList);
                    }
                })
                .catch(e => {
                    self.setState({ dataSource: [this.createNoResultsItem()], popoverOpen: true, isloading: false });
                });

        }
        else if (text.length >= (this.props.minSearchLength || 3)) {
            const completeURL = this.buildSearchUrl(searchURL, text);
            request
                .get(completeURL)
                .then((result) => {
                    const dataList = this.extractDataFromResult(result);
                    if (dataList.length == 0) {
                        self.setState({ dataSource: [this.createNoResultsItem()], popoverOpen: true, isloading: false });
                        return;
                    }

                    self.setState({ dataSource: dataList, popoverOpen: true, isloading: false });

                    if (this.props.supportsHierarchicalSearch) {
                        self.addNodeToSearchTree(dataList);
                    }
                })
                .catch(e => {
                    self.setState({ dataSource: [this.createNoResultsItem()], popoverOpen: true, isloading: false });
                });
        }
    }

    extractDataFromResult(result) {
        // Handle different result structures
        if (Array.isArray(result)) {
            return result;
        }
        if (result.addresses) {
            return result.addresses; // Address search format
        }
        if (result.data) {
            return result.data; // Generic data format
        }
        return result; // Assume result is the data itself
    }

    createNoResultsItem() {
        const noResultsProperty = this.props.noResultsProperty || 'buildingName';
        return { [noResultsProperty]: this.state.textNotFound };
    }

    buildSearchUrl(baseUrl, text) {
        const searchPath = this.props.searchPath || 'Find';
        const keywordParam = this.props.keywordParam || 'keyword';
        return baseUrl + searchPath + '?' + keywordParam + '=' + encodeURIComponent(text) + (this.props.extraQueryString || '');
    }

    buildContainerSearchUrl(baseUrl, containerId) {
        const containerPath = this.props.containerPath || 'FindInContainer';
        const containerParam = this.props.containerParam || 'containerId';
        return baseUrl + containerPath + '?' + containerParam + '=' + encodeURIComponent(containerId);
    }

    addNodeToSearchTree(nodes) {
        if (!this.props.supportsHierarchicalSearch) return;

        var newNode = {
            parent: currentNode,
            nodes: nodes
        };

        currentNode = newNode;
    }
    /**
     * Retrieve detailed item information based on the item's ID
     */
    retrieveItemDetails(itemId) {
        if (!this.props.retrievePath) {
            // If no retrieve path specified, select the item directly
            const item = this.state.dataSource.find(item => this.getItemId(item) === itemId);
            if (item) {
                this.props.onMenuItemSelected(item);
            }
            return;
        }

        const self = this;
        const searchURL = this.props.endPointUrl + this.props.retrievePath;
        const idParam = this.props.retrieveIdParam || 'id';
        const completeURL = searchURL + '?' + idParam + '=' + encodeURIComponent(itemId);

        const requestMethod = this.props.makeAuthRequest ? request.getAuth : request.get;

        requestMethod(completeURL)
            .then((result) => {
                const dataList = this.extractDataFromResult(result);
                if (dataList.length > 0) {
                    this.props.onMenuItemSelected(dataList[0]);
                }
            })
            .catch(e => {
                self.setState({ dataSource: [this.createNoResultsItem()], popoverOpen: true, isloading: false });

            });
    }

    handleInputChange(e) {
        if (this.props.onUpdateInput) {
            this.props.onUpdateInput(e.target.value);
        }

        this.setState({ popoverAnchorEl: e.target, textValue: e.target.value, isloading: true });
        clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
            this.doneTyping(this.state.textValue);
        }, 500);
    }

    handleOnFocus(e) {
        // console.log('handleOnFocus ')
        // if (this.skipFocus == true) {
        //     this.skipFocus = false;
        //     return;
        // }
        // // PE7 1RE
        // const self = this;
        // if (this.state.dataSource.length > 0) {
        //     if (this.state.dataSource[0].buildingName != this.state.textNotFound) {
        //         clearTimeout(this.timeout);
        //         this.timeout = setTimeout(() => {
        //             console.log('handleOnFocus ', this.textBoxRef)
        //             self.setState({ dataSource: this.state.dataSource, popoverOpen: true, isloading: false, popoverAnchorEl: this.textBoxRef });
        //         }, 300);
        //     }
        // }
    }

    handleOnBlur = (e) => {
        //this.setState({ popoverOpen: false, popoverAnchorEl: null });
        this.textBoxRef.focus();
    }

    getItemId(item) {
        return item[this.props.itemIdProperty || 'addressKey'] || item.id;
    }

    getItemType(item) {
        return item[this.props.itemTypeProperty || 'type'];
    }

    isSelectableItem(item) {
        if (this.props.isSelectableItem) {
            return this.props.isSelectableItem(item);
        }

        const itemType = this.getItemType(item);
        const selectableType = this.props.selectableItemType || 'Address';
        return itemType === selectableType || !itemType; // Default to selectable if no type
    }

    isContainerItem(item) {
        if (!this.props.supportsHierarchicalSearch) return false;

        if (this.props.isContainerItem) {
            return this.props.isContainerItem(item);
        }

        const itemType = this.getItemType(item);
        const selectableType = this.props.selectableItemType || 'Address';
        return itemType && itemType !== selectableType;
    }

    handleMenuItemSelected(itemIndex) {
        var item = this.state.dataSource[itemIndex];

        // Check if this is a container item that should trigger sub-search
        if (this.isContainerItem(item)) {
            this.doneTyping(this.state.textValue, this.getItemId(item));
            return;
        }

        // Check if this is a selectable item that needs detail retrieval
        if (this.isSelectableItem(item) && this.props.retrievePath) {
            this.retrieveItemDetails(this.getItemId(item));
        }
        else {
            // Direct selection
            this.props.onMenuItemSelected(item);
        }

        this.setState({ popoverOpen: false, popoverAnchorEl: null });
    }

    handleRequestClosePopover() {
        this.setState({ popoverOpen: false });
    }

    getMenuPositionProps() {
        const MARGIN = 8;
        const MAX = 450;
        if (!this.textBoxRef) return {};

        const rect = this.textBoxRef.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom - MARGIN;
        const spaceAbove = rect.top - MARGIN;
        const openAbove = spaceBelow < 150 && spaceAbove > spaceBelow;

        return {
            anchorReference: 'anchorPosition',
            anchorPosition: openAbove
                ? { top: rect.top + window.scrollY, left: rect.left + window.scrollX }
                : { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX },
            transformOrigin: openAbove
                ? { vertical: 'bottom', horizontal: 'left' }
                : { vertical: 'top', horizontal: 'left' },
            maxMenuHeight: Math.min(MAX, openAbove ? spaceAbove : spaceBelow)
        };
    }

    renderPopoverChildren() {
        const { anchorReference, anchorPosition, transformOrigin, maxMenuHeight } = this.getMenuPositionProps();
        return (
            <Menu
                slotProps={{
                    paper: {
                        style: {
                            maxHeight: maxMenuHeight || 450,
                            width: this.textBoxRef != undefined ? this.textBoxRef.offsetWidth : 0,
                            overflowX: 'auto'
                        }
                    }
                }}

                disableScrollLock={true}
                anchorReference={anchorReference}
                anchorPosition={anchorPosition}
                anchorEl={this.state.popoverAnchorEl}
                onClose={this.handleRequestClosePopover}
                open={this.state.popoverOpen}
                transformOrigin={transformOrigin || { vertical: 'top', horizontal: 'left' }}
            >
                {this.props.supportsHierarchicalSearch && currentNode.parent && (
                    <MenuItem className="menu-arrow" onClick={() => this.handleBack()}>
                        <div className='text-link'>{this.props.backButtonText || 'Back'}</div>
                    </MenuItem>
                )}
                {this.state.dataSource.map((item, index) => {
                    let displayText = this.props.formatMenuItem ? this.props.formatMenuItem(item).trim() : JSON.stringify(item);

                    // Clean up trailing punctuation if needed
                    if (this.props.cleanTrailingPunctuation !== false) {
                        let test = displayText.slice(-2);
                        if (test === '.,') {
                            displayText = displayText.substring(0, displayText.length - 1);
                        }
                    }

                    // Highlight search criteria in display text
                    const displayContent = this.state.textValue
                        ? applyHighlightToDisplay(displayText, this.state.textValue)
                        : displayText;

                    const showExpandIcon = this.props.supportsHierarchicalSearch && this.isContainerItem(item);

                    return <MenuItem className="search-results" key={index} onClick={() => this.handleMenuItemSelected(index)}>
                        <div className="address-item">
                            <div className="address-text">{displayContent}</div>
                            {showExpandIcon && <div>&gt;</div>}
                        </div>
                    </MenuItem>
                }
                )}
            </Menu>
        );
    }

    static defaultProps = {
        containerClassName: "content-block-form align-center",
        internalDivClassName: "form-control-80",
        placeholder: 'Enter search term...',
        inputClassName: 'form-control-grey',
        label: 'Search',
        isRequired: true,
        makeAuthRequest: false,
        supportsHierarchicalSearch: false,
        minSearchLength: 3,
        cleanTrailingPunctuation: true,
        noResultsMessage: 'No results were found. Please change your search criteria.',
        noResultsProperty: 'buildingName',
        selectableItemType: 'Address',
        itemIdProperty: 'addressKey',
        itemTypeProperty: 'type',
        keywordParam: 'keyword',
        searchPath: 'Find',
        containerPath: 'FindInContainer',
        containerParam: 'containerId',
        retrieveIdParam: 'addressId',
        backButtonText: 'Back'
    }

    render() {
        return (
            <div className={this.props.containerClassName}>
                <div className={`entry entry-search form-masked-label ${this.props.internalDivClassName}`}>
                    {this.state.isloading === true ? <div className="loader"></div> : null}
                    <input id={this.props.inputId || "filter-postcode"} type="search"
                        placeholder={this.props.placeholder}
                        className={this.props.inputClassName}
                        onChange={this.handleInputChange}
                        onFocus={this.handleOnFocus}
                        onBlur={this.handleOnBlur}
                        ref={textBox => this.textBoxRef = textBox}
                        value={this.state.textValue}
                        required={this.props.isRequired}
                    />
                    <label htmlFor={this.props.inputId || "filter-postcode"}>{this.props.label}</label>
                    {this.props.extraInfo}
                    {this.renderPopoverChildren()}
                </div>
            </div>
        );
    }
}
